import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Cloud, Wind, Droplets, Gauge, Send, Loader2, MapPin } from "lucide-react";

const WX_EMOJI = {
  0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️",
  45: "🌫️", 48: "🌫️",
  51: "🌦️", 53: "🌦️", 55: "🌧️",
  61: "🌧️", 63: "🌧️", 65: "⛈️",
  71: "🌨️", 73: "🌨️", 75: "❄️",
  80: "🌦️", 81: "🌧️", 82: "⛈️",
  95: "⛈️", 96: "⛈️", 99: "⛈️",
};

function aqiInfo(v) {
  if (v == null) return { label: "—", color: "text-slate-500", bg: "bg-slate-100" };
  if (v <= 50) return { label: "अच्छी", color: "text-emerald-700", bg: "bg-emerald-100" };
  if (v <= 100) return { label: "ठीक-ठाक", color: "text-amber-700", bg: "bg-amber-100" };
  if (v <= 150) return { label: "संवेदनशील", color: "text-orange-700", bg: "bg-orange-100" };
  if (v <= 200) return { label: "खराब", color: "text-red-700", bg: "bg-red-100" };
  if (v <= 300) return { label: "बहुत खराब", color: "text-purple-800", bg: "bg-purple-100" };
  return { label: "जोखिम", color: "text-rose-800", bg: "bg-rose-100" };
}

export default function CityWeatherCard({ city, onQuickSubmit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    setLoading(true); setError(false); setData(null);
    api.get("/city-weather", { params: { city } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city]);

  if (!city) return null;
  const aqi = aqiInfo(data?.aqi);
  const emoji = data?.weather_code != null ? (WX_EMOJI[data.weather_code] || "🌡️") : "🌡️";

  return (
    <div className="mb-6 rounded-xl overflow-hidden border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 shadow-sm" data-testid="city-weather-card">
      <div className="p-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-14 h-14 rounded-lg bg-white/70 backdrop-blur flex items-center justify-center text-4xl leading-none">{emoji}</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#EA580C] font-bold flex items-center gap-1">
              <MapPin className="w-3 h-3"/> अभी {city} में
            </div>
            <div className="font-devnagri-heading text-2xl font-black text-[#0F172A] leading-none mt-0.5">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline"/> :
               error ? <span className="text-slate-500 text-base">डेटा उपलब्ध नहीं</span> :
               <>{Math.round(data.temp)}<span className="text-lg">°C</span></>}
            </div>
            {data?.state && !error && <div className="text-[10px] text-slate-500 mt-0.5">{data.state}</div>}
          </div>
        </div>

        {data && !loading && !error && (
          <div className="flex items-center gap-2 flex-wrap">
            <Chip icon={<Cloud className="w-3 h-3"/>} label="मौसम" value={emoji}/>
            <Chip icon={<Droplets className="w-3 h-3"/>} label="नमी" value={`${data.humidity ?? "—"}%`}/>
            <Chip icon={<Wind className="w-3 h-3"/>} label="हवा" value={`${Math.round(data.wind || 0)} km/h`}/>
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ${aqi.bg} border border-white/60`} data-testid="city-aqi">
              <Gauge className={`w-3 h-3 ${aqi.color}`}/>
              <div className="leading-tight">
                <div className={`text-[8px] uppercase tracking-widest font-black ${aqi.color}`}>AQI</div>
                <div className={`text-xs font-black ${aqi.color}`}>{data.aqi ?? "—"} · {aqi.label}</div>
              </div>
            </div>
          </div>
        )}

        {onQuickSubmit && (
          <button
            onClick={() => onQuickSubmit(city)}
            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-[#EA580C] to-[#DC2626] hover:from-[#DC2626] hover:to-[#EA580C] text-white font-bold text-xs px-3 py-2 rounded-md shadow transition"
            data-testid="quick-submit-btn">
            <Send className="w-3.5 h-3.5"/> {city} की खबर भेजें
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ icon, label, value }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-white/60 backdrop-blur px-2.5 py-1.5 rounded-md border border-white/60">
      <span className="text-slate-500">{icon}</span>
      <div className="leading-tight">
        <div className="text-[8px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-xs font-black text-[#0F172A]">{value}</div>
      </div>
    </div>
  );
}
